import { useState, useEffect, useCallback, useRef } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Button } from "../ui/button";
import { ThemeToggle } from "../theme-toggle";
import { Menu, X, Github, Accessibility } from "lucide-react";
import { cn } from "@/lib/utils";
import { motion, AnimatePresence, Variants } from "framer-motion";
import AccessibilityMenu from '../AccessibilityMenu';

/**
 * Header component props
 */
interface HeaderProps {
  /** Whether the page has been scrolled */
  scrolled: boolean;
}

/**
 * Navigation item type
 */
interface NavItem {
  path: string;
  label: string;
}

// Navigation items configuration
const NAV_ITEMS: NavItem[] = [
  { path: "/", label: "Home" },
  { path: "/projects", label: "Projects" },
  { path: "/experience", label: "Experience" },
  { path: "/contact", label: "Contact" }
];

/**
 * Header Component
 * 
 * Responsive header with desktop and mobile navigation
 */
const Header = ({ scrolled }: HeaderProps) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);
  const location = useLocation();
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const [accessibilityMenuOpen, setAccessibilityMenuOpen] = useState(false);

  // Close mobile menu handler
  const closeMobileMenu = useCallback(() => {
    setMobileMenuOpen(false);
  }, []);

  // Toggle mobile menu handler
  const toggleMobileMenu = useCallback(() => {
    setMobileMenuOpen(prev => !prev);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    closeMobileMenu();
  }, [location, closeMobileMenu]);

  // Handle clicks outside of mobile menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (mobileMenuOpen && mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) {
        closeMobileMenu();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [mobileMenuOpen, closeMobileMenu]);

  // Handle keyboard events for accessibility
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && mobileMenuOpen) {
        closeMobileMenu();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [mobileMenuOpen, closeMobileMenu]);

  // Control body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [mobileMenuOpen]);

  // Animation variants
  const navItemVariants: Variants = {
    hidden: { opacity: 0, y: -10 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.5,
        ease: [0.22, 1, 0.36, 1]
      }
    }),
    exit: { opacity: 0, y: -10, transition: { duration: 0.3 } }
  };

  const mobileMenuVariants: Variants = {
    hidden: { opacity: 0, x: "-100%" },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.4,
        ease: [0.22, 1, 0.36, 1],
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    },
    exit: {
      opacity: 0,
      x: "-100%",
      transition: {
        duration: 0.3,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  const mobileNavItemVariants: Variants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { 
        duration: 0.4,
        ease: [0.22, 1, 0.36, 1]
      }
    },
    exit: {
      opacity: 0,
      x: -20,
      transition: { 
        duration: 0.3,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  const logoVariants: Variants = {
    initial: { opacity: 0, x: -20 },
    animate: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  return (
    <>
      {/* Floating Accessibility Button */}
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.5 }}
        className="fixed left-4 bottom-4 z-50"
      >
        <Button
          variant="outline"
          size="icon"
          className="rounded-full bg-background/80 backdrop-blur-md shadow-lg hover:bg-background/90 transition-all duration-300"
          onClick={() => setAccessibilityMenuOpen(true)}
          aria-label="Open accessibility menu"
        >
          <Accessibility className="h-5 w-5" />
        </Button>
      </motion.div>

      <header
        className={cn(
          "sticky top-0 z-40 w-full transition-all duration-300",
          scrolled
            ? "bg-background/80 backdrop-blur-md border-b shadow-sm"
            : "bg-transparent"
        )}
      >
        <div className="container-custom flex h-16 items-center justify-between">
          {/* Logo */}
          <motion.div 
            className="flex items-center gap-2"
            variants={logoVariants}
            initial="initial"
            animate="animate"
          >
            <Link 
              to="/" 
              className="text-2xl font-bold tracking-tight relative"
              onClick={closeMobileMenu}
              aria-label="Tomer - Homepage"
            >
              <span className="text-crimson">T</span>omer
              <motion.span 
                className="text-crimson"
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [1, 0.8, 1],
                  transition: { 
                    duration: 2, 
                    repeat: Infinity,
                    repeatType: "reverse" 
                  }
                }}
                aria-hidden="true"
              >
                .
              </motion.span>
            </Link>
          </motion.div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6" aria-label="Main navigation">
            {NAV_ITEMS.map((item, i) => (
              <DesktopNavItem key={item.path} item={item} index={i} />
            ))}
            
            <motion.div
              custom={NAV_ITEMS.length}
              initial="hidden"
              animate="visible"
              variants={navItemVariants}
            >
              <ThemeToggle />
            </motion.div>
            
            <motion.div
              custom={NAV_ITEMS.length + 1}
              initial="hidden"
              animate="visible"
              variants={navItemVariants}
              whileHover={{ scale: 1.05 }}
            >
              <Button 
                asChild
                variant="default" 
                size="sm"
                className="bg-crimson hover:bg-crimson/90"
              >
                <a
                  href="https://github.com/Tgoldi"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2"
                  aria-label="GitHub profile"
                >
                  <Github className="h-4 w-4" />
                  <span>GitHub</span>
                </a>
              </Button>
            </motion.div>
          </nav>

          {/* Mobile Navigation Controls */}
          <div className="flex md:hidden items-center gap-2">
            <ThemeToggle />
            <motion.button
              className="p-2 rounded-lg hover:bg-muted transition-colors"
              onClick={toggleMobileMenu}
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
              whileTap={{ scale: 0.95 }}
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </motion.button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            ref={mobileMenuRef}
            className="fixed inset-0 z-50 bg-background/95 backdrop-blur-md md:hidden"
            variants={mobileMenuVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <div className="container-custom h-full flex flex-col">
              <div className="flex justify-end p-4">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={closeMobileMenu}
                  className="rounded-full"
                  aria-label="Close menu"
                >
                  <X className="h-6 w-6" />
                </Button>
              </div>
              
              <nav className="flex-1 flex flex-col items-center justify-center gap-8" aria-label="Mobile navigation">
                {NAV_ITEMS.map((item) => (
                  <MobileNavItem key={item.path} item={item} onClose={closeMobileMenu} />
                ))}
                
                <div className="flex items-center gap-4 mt-8">
                  <Button 
                    asChild
                    variant="default" 
                    size="sm"
                    className="bg-crimson hover:bg-crimson/90"
                  >
                    <a
                      href="https://github.com/Tgoldi"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2"
                      aria-label="GitHub profile"
                    >
                      <Github className="h-4 w-4" />
                      <span>GitHub</span>
                    </a>
                  </Button>
                </div>
              </nav>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Accessibility Menu */}
      <AccessibilityMenu 
        isOpen={accessibilityMenuOpen} 
        onClose={() => setAccessibilityMenuOpen(false)} 
      />
    </>
  );
};

/**
 * Desktop Navigation Item Component
 */
interface DesktopNavItemProps {
  item: NavItem;
  index: number;
}

const DesktopNavItem = ({ item, index }: DesktopNavItemProps) => (
  <motion.div
    custom={index}
    initial="hidden"
    animate="visible"
    variants={{
      hidden: { opacity: 0, y: -10 },
      visible: (i) => ({
        opacity: 1,
        y: 0,
        transition: {
          delay: i * 0.1,
          duration: 0.5,
          ease: [0.22, 1, 0.36, 1]
        }
      })
    }}
  >
    <NavLink
      to={item.path}
      className={({ isActive }) =>
        cn(
          "text-sm font-medium transition-colors relative group",
          isActive ? "text-crimson" : "text-foreground/70 hover:text-foreground"
        )
      }
    >
      {item.label}
      <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-crimson group-hover:w-full transition-all duration-300 ease-in-out" />
    </NavLink>
  </motion.div>
);

/**
 * Mobile Navigation Item Component
 */
interface MobileNavItemProps {
  item: NavItem;
  onClose: () => void;
}

const MobileNavItem = ({ item, onClose }: MobileNavItemProps) => (
  <motion.div
    variants={{
      hidden: { opacity: 0, x: -20 },
      visible: {
        opacity: 1,
        x: 0,
        transition: { 
          duration: 0.4,
          ease: [0.22, 1, 0.36, 1]
        }
      }
    }}
    className="w-full"
  >
    <NavLink
      to={item.path}
      onClick={() => {
        // Close menu immediately
        onClose();
        // Force navigation
        window.location.href = item.path;
      }}
      className={({ isActive }) =>
        cn(
          "text-2xl font-bold w-full block text-center py-2 transition-colors",
          isActive ? "text-crimson" : "text-foreground/80 hover:text-foreground"
        )
      }
    >
      {item.label}
    </NavLink>
  </motion.div>
);

export default Header;
